import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getProductUrl } from '../utils/productUrl';
import { Search, Menu, X, ShoppingCart, User, LogOut } from "lucide-react";
import { useContext } from "react";
import { ProductContext } from "./context/ProductContext";
import { useAuth } from "./context/AuthContext";
import { useCategories } from "./context/CategoriesContext";
import { useCart } from "./context/CartContext";

const Header = () => {
  const { products: contextProducts } = useContext(ProductContext);
  const { categories, loading, error } = useCategories();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  // Search functionality states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const desktopSearchRef = useRef(null);

  const { user, logout } = useAuth();
  const { cart } = useCart();

  // Calculate total items in cart
  const cartItemsCount = cart.reduce((total, item) => total + item.quantity, 0);

  // Helper function to check if a route is active
  const isActiveRoute = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Helper function to check if any category route is active
  const isCategoryRouteActive = () => {
    return (
      location.pathname.startsWith("/category/") ||
      location.pathname === "/categories"
    );
  };

  // Enhanced navigation function with scroll to top
  const handleNavigation = (path) => {
    navigate(path);
    // Scroll to top after navigation
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
    setIsSearchOpen(false);
    setShowSearchDropdown(false);
    setSearchQuery("");
  };

  const [productId, setProductId] = useState(null);

  useEffect(() => {
    const savedId = localStorage.getItem("activeProductId");
    setProductId(savedId);
  }, []);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, contextProducts, categories]);

  const performSearch = (query) => {
    if (!contextProducts?.length) return;
    setIsSearchLoading(true);

    const q = query.toLowerCase();

    // Build a quick category id→name map for search matching
    const catMap = {};
    categories.forEach((c) => { catMap[String(c.id)] = c.title || ''; });

    const filtered = contextProducts.filter((p) => {
      const name = (p.name || '').toLowerCase();
      const desc = (p.description || '').toLowerCase();
      const cat  = (catMap[String(p.categoryId)] || '').toLowerCase();
      return name.includes(q) || desc.includes(q) || cat.includes(q);
    });

    setSearchResults(filtered.slice(0, 8));
    setShowSearchDropdown(true);
    setIsSearchLoading(false);
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Enhanced product selection — uses slug when available
  const handleProductSelect = (productId) => {
    const product = contextProducts?.find(p => String(p.id) === String(productId));
    handleNavigation(product ? getProductUrl(product) : `/product/${productId}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    if (searchResults.length === 1) {
      handleProductSelect(searchResults[0].id);
    } else {
      // Navigate to shop with search applied
      handleNavigation(`/shop?search=${encodeURIComponent(q)}`);
    }
  };

  const toggleMobileMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen((prev) => !prev);
    setIsSearchOpen(false);
    setIsCategoryOpen(false);
    setShowSearchDropdown(false);
  };

  const toggleSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSearchOpen((prev) => !prev);
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
    if (!isSearchOpen) {
      setShowSearchDropdown(false);
      setSearchQuery("");
    }
  };

  const toggleCategory = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCategoryOpen((prev) => !prev);
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
    setShowSearchDropdown(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Search shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    const handleShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setIsSearchOpen(true);
        setIsMobileMenuOpen(false);
        setIsCategoryOpen(false);
        setShowSearchDropdown(false);
        setSearchQuery("");
        // Focus the visible input after state update
        setTimeout(() => {
          const input =
            document.querySelector(".desktop-search-input") ||
            document.querySelector(".mobile-search-input");
          input?.focus();
        }, 50);
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, []);

  // Close menus when clicking outside or on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
        setIsCategoryOpen(false);
        setShowSearchDropdown(false);
      }
    };

    const handleClickOutside = (e) => {
      // Only close if clicking outside the menu containers
      if (
        isMobileMenuOpen &&
        !e.target.closest(".mobile-menu-container") &&
        !e.target.closest(".mobile-menu-toggle")
      ) {
        setIsMobileMenuOpen(false);
      }
      if (isCategoryOpen && !e.target.closest(".category-dropdown-container")) {
        setIsCategoryOpen(false);
      }
      if (
        showSearchDropdown &&
        !e.target.closest(".search-container") &&
        !e.target.closest(".search-icon-button")
      ) {
        setShowSearchDropdown(false);
      }
      if (
        isSearchOpen &&
        !e.target.closest(".search-container") &&
        !e.target.closest(".search-icon-button")
      ) {
        setIsSearchOpen(false);
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isMobileMenuOpen, isCategoryOpen, showSearchDropdown, isSearchOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Enhanced logout with scroll to top
  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    if (window.confirm("Are you sure you want to logout?")) {
      logout();
      navigate("/signin", { replace: true });
      // Scroll to top after logout navigation
      window.scrollTo(0, 0);
    }
  };

  // Debug log for mobile menu state
  useEffect(() => {
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav
        className="w-full shadow-lg overflow-visible bg-[#3a3938]"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex justify-between items-center w-full gap-2 px-4 md:px-6 lg:px-14 py-2  mx-auto">
          {/* Left Navigation - Desktop Only */}
          <div className="hidden lg:flex items-center gap-6">
            {[
              { label: 'Products', path: '/products' },
              { label: 'Blog', path: '/blog' },
              { label: 'Contact Us', path: '/contact' },
            ].map(({ label, path }) => (
              <button
                key={path}
                onClick={() => handleNavigation(path)}
                className={`relative text-sm font-medium pb-0.5 transition-all duration-200 group ${
                  isActiveRoute(path) ? 'text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                {label}
                <span className={`absolute bottom-0 left-0 h-0.5 rounded-full bg-orange-400 transition-all duration-300 ${
                  isActiveRoute(path) ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </button>
            ))}
          </div>

          {/* Center - Logo */}
          <div className="flex-shrink-0 relative lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
            <button
              onClick={() => handleNavigation("/")}
              className={`hover:opacity-100 transition-all mt-1 duration-300 relative`}
              aria-label="Manaprint Home"
            >
              <img
                src="https://cdn.acsdev.in/ManaPrint/manaprint_logo_tras.png"
                alt="Manaprint Logo"
                className="h-12 sm:h-14 md:h-16 lg:h-22 xl:h-20 w-auto scale-110 object-contain rounded-2xl"
              />
            </button>
          </div>

          {/* Right - Icons (Desktop) */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Search */}
            <div className="flex items-center gap-2 relative">
              <div className={`transition-all duration-300 ease-in-out ${isSearchOpen ? "max-w-xs opacity-100" : "max-w-0 opacity-0 overflow-hidden"}`}>
                <div className="relative search-container" ref={desktopSearchRef}>
                  <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 py-1.5 pr-3 pl-4 bg-white/10 border border-white/20 rounded-full w-64 focus-within:bg-white/15 focus-within:border-white/40 transition-all duration-200">
                    <input
                      type="search"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      className="bg-transparent border-none outline-none flex-1 min-w-0 placeholder-white/40 text-white text-sm desktop-search-input"
                      aria-label="Search products"
                      autoFocus={isSearchOpen}
                    />
                    <button type="submit" aria-label="Search" className="text-white/50 hover:text-white transition-colors flex-shrink-0">
                      <Search size={15} />
                    </button>
                  </form>
                </div>
              </div>

              <div className="relative flex-shrink-0">
                <button
                  onClick={toggleSearch}
                  className="search-icon-button w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:bg-white/10"
                  aria-label="Toggle search (Ctrl+K)"
                >
                  {isSearchOpen ? <X size={20} className="text-white" /> : <Search size={20} className="text-white/80" />}
                </button>
              </div>

              {/* Search Dropdown */}
              {showSearchDropdown && isSearchOpen && (
                <div className="absolute top-full right-0 mt-3 bg-white border border-gray-200 rounded-2xl shadow-2xl z-[9999] max-h-80 overflow-y-auto w-80">
                  {isSearchLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      <div className="inline-flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600" />
                        Searching...
                      </div>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((product) => {
                        const catName = categories.find(c => String(c.id) === String(product.categoryId))?.title || '';
                        return (
                          <button key={product.id} onClick={() => handleProductSelect(product.id)}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0">
                            <div className="flex items-center gap-3">
                              {product.image && <img src={product.image} alt={product.name} className="w-10 h-10 object-contain rounded-lg bg-gray-100 flex-shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-800 text-sm truncate">{product.name}</h4>
                                {catName && <p className="text-xs text-gray-400 mt-0.5">{catName}</p>}
                              </div>
                              {product.offerPrice > 0 && product.offerPrice < product.price
                                ? <span className="text-xs font-semibold text-red-500 flex-shrink-0">₹{product.offerPrice}</span>
                                : product.price ? <span className="text-xs font-semibold text-gray-700 flex-shrink-0">₹{product.price}</span> : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : searchQuery.trim() ? (
                    <div className="p-6 text-center text-gray-500">
                      <Search size={24} className="text-gray-300 mx-auto mb-2" />
                      <p className="text-sm font-medium">No products found</p>
                      <p className="text-xs text-gray-400 mt-1">Try different keywords</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Divider */}
            {user && <div className="w-px h-5 bg-white/20 mx-1" />}

            {/* Cart */}
            {user && (
              <button onClick={() => handleNavigation("/cart")}
                className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${isActiveRoute("/cart") ? "bg-white/20 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}
                aria-label="Shopping Cart">
                <ShoppingCart size={20} />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-5 flex items-center justify-center min-w-[16px]">
                    {cartItemsCount > 99 ? '99+' : cartItemsCount}
                  </span>
                )}
              </button>
            )}

            {/* Profile */}
            {user && (
              <button onClick={() => handleNavigation("/profile")}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${isActiveRoute("/profile") ? "bg-white/20 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}
                aria-label="Profile">
                <User size={20} />
              </button>
            )}

            {/* Logout / Sign In */}
            {user ? (
              <button onClick={handleLogout}
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-all duration-200"
                aria-label="Logout">
                <LogOut size={20} />
              </button>
            ) : (
              <button onClick={() => handleNavigation("/signin")}
                className="ml-2 px-5 py-2 text-sm font-semibold rounded-full border border-white/30 text-white hover:bg-white hover:text-gray-900 transition-all duration-200 whitespace-nowrap">
                Sign In
              </button>
            )}
          </div>

          {/* Tablet + Mobile Controls */}
          <div className="flex lg:hidden items-center gap-1 sm:gap-2 flex-shrink-0 relative">
            {/* Mobile Search Bar — hidden on desktop (desktop has inline search) */}
              <div className={`lg:hidden w-full px-0 sm:px-4 transition-all duration-300 ease-in-out origin-right relative ${isSearchOpen ? "max-h-96 w-40 opacity-100 scale-x-100 " : "max-h-0 w-0 opacity-0 scale-x-0 overflow-hidden"}`}>
                <div className="relative search-container" ref={mobileSearchRef}>
                  <form onSubmit={handleSearchSubmit}
                    className="flex items-center gap-2 py-2 pr-3 pl-4 bg-white/10 border border-white/20 rounded-full w-full focus-within:bg-white/15 focus-within:border-white/40 transition-all duration-200">
                    <input
                      type="search"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      className="bg-transparent border-none outline-none flex-1 min-w-0 placeholder-white/40 text-white text-sm mobile-search-input"
                      aria-label="Search products"
                      autoFocus={isSearchOpen}
                    />
                    <button type="submit" aria-label="Search" className="text-white/50 hover:text-white transition-colors flex-shrink-0">
                      <Search size={15} />
                    </button>
                  </form>

                  {showSearchDropdown && isSearchOpen && (
                    <div className="absolute top-full left-0 right-0 mt-3 bg-white border border-gray-200 rounded-2xl shadow-2xl z-[9999] max-h-72 overflow-y-auto">
                      {isSearchLoading ? (
                        <div className="p-4 text-center text-gray-500">
                          <div className="inline-flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600" />
                            Searching...
                          </div>
                        </div>
                      ) : searchResults.length > 0 ? (
                        <div className="py-2">
                          {searchResults.map((product) => {
                            const catName = categories.find(c => String(c.id) === String(product.categoryId))?.title || '';
                            return (
                              <button key={product.id} onClick={() => handleProductSelect(product.id)}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0">
                                <div className="flex items-center gap-3">
                                  {product.image && <img src={product.image} alt={product.name} className="w-9 h-9 object-contain rounded-lg bg-gray-100 flex-shrink-0" />}
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-800 text-sm truncate">{product.name}</h4>
                                    {catName && <p className="text-xs text-gray-400">{catName}</p>}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ) : searchQuery.trim() ? (
                        <div className="p-5 text-center text-gray-400">
                          <Search size={20} className="mx-auto mb-1 text-gray-300" />
                          <p className="text-sm">No products found</p>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            <button onClick={toggleSearch}
              className="search-icon-button w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-all duration-200"
              aria-label="Toggle search">
              {isSearchOpen ? <X size={18} className="text-white" /> : <Search size={18} />}
            </button>
            <button onClick={toggleMobileMenu}
              className="mobile-menu-toggle w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-all duration-200"
              aria-label="Toggle navigation menu" aria-expanded={isMobileMenuOpen}>
              {isMobileMenuOpen ? <X size={18} className="text-white" /> : <Menu size={18} />}
            </button>
          </div>
        </div>


      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm" onClick={closeMobileMenu}>
          <div
            className="mobile-menu-container fixed top-0 right-0 h-full w-72 max-w-[82vw] shadow-2xl overflow-y-auto"
            style={{ backgroundColor: '#2d2c2b' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">

              {/* Header */}
              <div className="flex justify-between items-center px-6 py-5 border-b border-white/10">
                <span className="text-sm font-semibold uppercase tracking-widest text-white/40">Menu</span>
                <button onClick={closeMobileMenu}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:bg-white/10 hover:text-white transition-all duration-200"
                  aria-label="Close menu">
                  <X size={18} />
                </button>
              </div>

              {/* Nav links */}
              <div className="flex flex-col py-3 flex-1">
                {[
                  { label: 'Home', path: '/' },
                  { label: 'Products', path: '/products' },
                  { label: 'Blog', path: '/blog' },
                  { label: 'Contact Us', path: '/contact' },
                ].map(({ label, path }) => (
                  <button key={path} onClick={() => handleNavigation(path)}
                    className={`flex items-center px-6 py-3.5 text-left transition-colors duration-200 text-sm font-medium ${
                      isActiveRoute(path)
                        ? 'text-white border-l-2 border-orange-400 bg-white/5'
                        : 'text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                    }`}>
                    {label}
                  </button>
                ))}

                {/* Divider */}
                {user && <div className="mx-6 my-3 h-px bg-white/10" />}

                {user && (
                  <button onClick={() => handleNavigation("/cart")}
                    className={`flex items-center gap-3 px-6 py-3.5 text-left text-sm font-medium transition-colors duration-200 ${
                      isActiveRoute("/cart") ? 'text-white bg-white/5 border-l-2 border-orange-400' : 'text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                    }`}>
                    <div className="relative">
                      <ShoppingCart size={16} />
                      {cartItemsCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] tracking-tight font-bold rounded-full h-3 w-4 flex text-center items-center justify-center min-w-[10px]">
                          {cartItemsCount > 9 ? '9+' : cartItemsCount}
                        </span>
                      )}
                    </div>
                    Cart
                  </button>
                )}
                {user && (
                  <button onClick={() => handleNavigation("/profile")}
                    className={`flex items-center gap-3 px-6 py-3.5 text-left text-sm font-medium transition-colors duration-200 ${
                      isActiveRoute("/profile") ? 'text-white bg-white/5 border-l-2 border-orange-400' : 'text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                    }`}>
                    <User size={16} />
                    Profile
                  </button>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-5 border-t border-white/10">
                {user ? (
                  <button onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-red-400 border border-red-400/30 rounded-full hover:bg-red-400/10 transition-all duration-200">
                    <LogOut size={16} />
                    Logout
                  </button>
                ) : (
                  <button onClick={() => handleNavigation("/signin")}
                    className="w-full flex items-center justify-center py-3 text-sm font-semibold text-white border border-white/30 rounded-full hover:bg-white hover:text-gray-900 transition-all duration-200">
                    Sign In
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Category Mega Menu Backdrop */}
      {isCategoryOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 z-40"
          onClick={() => setIsCategoryOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
